import { useState, useEffect, useRef } from 'react';

export const usePopoverManager = ({
  enabled = false,
  shouldShow = () => false,
  dropdownOpen = false,
  startIndex = 0,
  initialDelay = 5000,
  restorationDelay = 1500,
  onPopoverStateChange = () => {}
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownInteracted, setDropdownInteracted] = useState(false);
  const anchorRef = useRef(null);

  // Handle popover state changes
  useEffect(() => {
    let initialTimer;
    let restorationTimer;

    // Short circuit if feature is disabled
    if (!enabled) {
      return () => {}; // Return empty cleanup function
    }

    // Only evaluate shouldShow when we know the feature is enabled
    const shouldShowPopover = shouldShow();

    // Initial popover display after delay
    if (anchorRef.current && shouldShowPopover && !isOpen && !dropdownOpen) {
      initialTimer = setTimeout(() => {
        setAnchorEl(anchorRef.current);
        setIsOpen(true);
      }, initialDelay);
    }

    // Popover restoration after dropdown interaction
    if (!dropdownOpen && dropdownInteracted && anchorEl && shouldShowPopover) {
      restorationTimer = setTimeout(() => {
        setIsOpen(true);
        setDropdownInteracted(false);
      }, restorationDelay);
    }

    // Cleanup timers
    return () => {
      clearTimeout(initialTimer);
      clearTimeout(restorationTimer);
    };
  }, [
    enabled,
    startIndex,
    isOpen,
    dropdownOpen,
    dropdownInteracted,
    anchorEl,
    shouldShow,
    initialDelay,
    restorationDelay
  ]);

  // Notify parent component of state changes
  useEffect(() => {
    onPopoverStateChange({ isOpen, anchorEl });
  }, [isOpen, anchorEl, onPopoverStateChange]);

  const open = () => {
    if (anchorRef.current) {
      setAnchorEl(anchorRef.current);
      setIsOpen(true);
    }
  };

  const close = () => {
    setIsOpen(false);
    setAnchorEl(null);
  };

  const handleDropdownInteraction = () => {
    setDropdownInteracted(true);
    if (isOpen) {
      setIsOpen(false);
    }
  };

  const resetInteraction = () => {
    setDropdownInteracted(false);
  };

  const handleCloseEvent = (event, closeHandler) => {
    if (closeHandler) {
      closeHandler(event, anchorRef, ({ closePopover, resetInteraction: shouldReset }) => {
        if (closePopover) {
          close();
        }
        if (shouldReset) {
          resetInteraction();
        }
      });
    } else {
      close();
    }
  };

  return {
    isOpen,
    anchorEl,
    dropdownInteracted,
    anchorRef,
    open,
    close,
    handleDropdownInteraction,
    resetInteraction,
    handleCloseEvent,
    setAnchorEl,
    setDropdownInteracted
  };
};